import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { WhatsAppFab } from "../components/WhatsAppFab";
import { useLang } from "../context/LanguageContext";
import { LEGAL_COPY } from "../lib/legalCopy";
import { useSeo } from "../lib/useSeo";

/**
 * Generic renderer for legal pages. `which` picks which copy block to use
 * from LEGAL_COPY (`privacy` or `terms`). Both pages share the same layout.
 */
const LegalPage = ({ which, testId }) => {
  const { lang } = useLang();
  const root = LEGAL_COPY[lang] || LEGAL_COPY.pt;
  const t = root[which];
  useSeo({ title: t.pageTitle });

  return (
    <div className="min-h-screen bg-[#b6d6ec]">
      <Header />

      <main
        data-testid={testId}
        className="mx-auto max-w-3xl px-6 pb-24 pt-12 md:px-12 md:pt-16 lg:px-16 lg:pb-32 lg:pt-20"
      >
        <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-blue-700">
          {t.overline}
        </div>
        <h1 className="font-heading mt-4 text-3xl font-black leading-tight tracking-tight text-slate-900 md:text-4xl lg:text-5xl">
          {t.title}
        </h1>
        <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
          {t.lastUpdate}
        </div>

        <p className="mt-8 text-base leading-relaxed text-slate-700 md:text-lg">
          {t.intro}
        </p>

        <div className="mt-10 space-y-8">
          {t.sections.map((s) => (
            <section key={s.h} className="border-l-2 border-blue-500 pl-5">
              <h2 className="font-heading text-lg font-bold text-slate-900 md:text-xl">
                {s.h}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-700 md:text-base">
                {s.p}
              </p>
            </section>
          ))}
        </div>

        <div className="mt-14 border-t border-slate-300 pt-8">
          <Link
            to="/"
            data-testid="legal-back-link"
            className="font-mono text-xs uppercase tracking-[0.22em] text-blue-700 transition hover:text-blue-900"
          >
            {root.back}
          </Link>
        </div>
      </main>

      <Footer />
      <WhatsAppFab />
    </div>
  );
};

export const PrivacyPolicy = () => (
  <LegalPage which="privacy" testId="privacy-page" />
);

export const Terms = () => <LegalPage which="terms" testId="terms-page" />;
